import React from 'react';
import { Box, IconButton, Typography, createTheme } from '@mui/material';
import { ArrowForwardIos } from '@mui/icons-material';

import { FCheckbox, FHButton } from '../../../base';
import InvoiceClaimDetailsTableRow from './InvoiceClaimDetailsTableRow';
import InvoiceListFooter from './InvoiceListFooter';
import InvoiceReadOnlyPaymentTableFooter from './InvoiceReadOnlyPaymentTableFooter';
import InvoiceClaimDetailsTableFooter from './InvoiceClaimDetailsTableFooter';
import { InvoiceStatusRenderer } from './InvoiceStatusRender';
import InvoiceHistoryTableRow from './InvoiceHistoryTableRow';
import { FHExcelCellInputComponentTypes } from '../../../base/FHExcelTable';
import {
  FORTA_BLACK,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_GRAY2,
  FORTA_SUCCESS,
  FORTA_WHITE,
  FORTA_YELLOW,
} from '../../../../utils/colors';
import { primaryTheme } from '../../../../utils/theme';
import { getThemeColor } from '../../../../utils/common';
import {
  dateFormatConverter,
  usDollarFormatter,
  history,
  formatEntityId,
} from '../../../../helpers';
import { InvoicePaymentTypeEnum } from '../claim-info/claim-payments/constants';
import {
  API_DATE_FORMAT,
  DATE_DISPLAY_FORMAT,
  ENTITY_PREFIX,
  InvoicePaymentStatus,
  InvoiceStatus,
} from '../../../../constants';
import SortImg from '../../../../assets/images/sort.svg';
import { getDueInDaysLabel } from './utils';

import type { Themes } from '../../../../constants';
import type { FHExcelTableColumnDef } from '../../../base/FHExcelTable';
import type { IInvoiceHistoryTableRowData } from './InvoiceHistoryTableRow';
import type { IInvoiceClaimDetailsTableRowData } from './InvoiceClaimDetailsTableRow';
import type { TFunction } from 'i18next';
import type { MUIDataTableOptions, Responsive, SelectableRows } from 'mui-datatables';
import type { Moment } from 'moment';

export const getBuildInvoiceTableOptions = (
  onSelectRow: (selected: any, claimId: number) => void,
) => ({
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: false,
  fixedHeader: false,
  sort: true,
  download: false,
  responsive: 'standard' satisfies Responsive,
  selectableRows: 'none' satisfies SelectableRows,
  tableBodyHeight: '100%',
  textLabels: {
    body: {
      noMatch: 'No claims found for the selected client',
    },
  },

  customRowRender: (data: any[]) => {
    const [selected, is_invoiceable, datesOfService, id, code, patient_responsibility, invoice_id] =
      data;

    const invoiceData: IInvoiceHistoryTableRowData = {
      selected,
      is_invoiceable,
      datesOfService,
      id,
      code,
      patient_responsibility,
      invoice_id,
    };

    return <InvoiceHistoryTableRow key={id} data={invoiceData} onSelectRow={onSelectRow} />;
  },
});

export const getBuildInvoiceTableColumns = (
  selectedAll: boolean,
  onSelectAll: (selected: boolean) => void,
) => [
  {
    name: 'selected',
    options: {
      filter: false,
      sort: false,
      customHeadLabelRender: () => (
        <FCheckbox
          coloredBackground
          iconStyles={{ boxShadow: 'none', border: `1px solid ${FORTA_BLUE.light2}` }}
          checked={selectedAll}
          onChange={(e) => onSelectAll(e.target.checked)}
          data-testid="selectAllCheckbox"
        />
      ),
    },
  },
  {
    name: 'is_invoiceable',
    label: 'Ready to invoice',
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          Ready to invoice
        </Typography>
      ),
    },
  },
  {
    name: 'datesOfService',
    label: 'Dates of service',
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          Dates of service
        </Typography>
      ),
    },
  },
  {
    name: 'id',
    label: 'Claim ID',
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          Claim ID
        </Typography>
      ),
    },
  },
  {
    name: 'code',
    label: 'Code',
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          Code
        </Typography>
      ),
    },
  },
  {
    name: 'patient_responsibility',
    label: 'Responsibility',
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          Responsibility
        </Typography>
      ),
    },
  },
  {
    name: 'invoice_id',
    label: 'Invoice Id',
    options: {
      filter: false,
      sort: true,
      display: false,
    },
  },
];

const COMMON_TABLE_OPTIONS = {
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: false,
  fixedHeader: false,
  sort: true,
  download: false,
  responsive: 'standard' satisfies Responsive,
  selectableRows: 'none' satisfies SelectableRows,
  tableBodyHeight: '100%',
  textLabels: {
    body: {
      noMatch: 'No data',
    },
  },
};

export const getInvoiceReadOnlyPaymentsOptions = (total: number) => ({
  ...COMMON_TABLE_OPTIONS,
  customTableBodyFooterRender: () => <InvoiceReadOnlyPaymentTableFooter total={total} />,
});

export const getInvoiceClaimDetailsTableOptions = (
  data: {
    billed: number;
    insPaid: number;
    adjustment: number;
    deductible: number;
    coIns: number;
    coPay: number;
    balanceDue: number;
    discount: number | null;
  },
  is_any_claim_paid_to_subscriber: boolean,
) => ({
  ...COMMON_TABLE_OPTIONS,
  customRowRender: (data: any[]) => {
    const [
      datesOfService,
      description,
      claim_id,
      billed,
      amount_insurance_paid,
      adjustments,
      deductible,
      co_insurance,
      co_pay,
      balance,
      is_amount_paid_to_subscriber,
    ] = data;

    const invoiceData: IInvoiceClaimDetailsTableRowData = {
      datesOfService,
      description,
      claim_id,
      billed,
      amount_insurance_paid,
      adjustments,
      deductible,
      co_insurance,
      co_pay,
      balance,
      is_amount_paid_to_subscriber,
    };

    return <InvoiceClaimDetailsTableRow key={claim_id} data={invoiceData} />;
  },
  customTableBodyFooterRender: () => {
    return (
      <InvoiceClaimDetailsTableFooter
        data={data}
        is_any_claim_paid_to_subscriber={is_any_claim_paid_to_subscriber}
      />
    );
  },
});

export const getInvoiceTableColumns = (t: TFunction<'invoice'>) => [
  {
    name: 'datesOfService',
    label: t('invoiceServiceTable.datesOfService'),
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.datesOfService')}
        </Typography>
      ),
    },
  },
  {
    name: 'description',
    label: t('invoiceServiceTable.description'),
    options: {
      filter: false,
      sort: true,
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.description')}
        </Typography>
      ),
    },
  },
  {
    name: 'claim_id',
    label: 'Claim ID',
    options: {
      display: false,
    },
  },
  {
    name: 'billed',
    label: t('invoiceServiceTable.billed'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.billed')}
        </Typography>
      ),
    },
  },
  {
    name: 'amount_insurance_paid',
    label: t('invoiceServiceTable.insPaid'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.insPaid')}
        </Typography>
      ),
    },
  },
  {
    name: 'adjustments',
    label: t('invoiceServiceTable.adjustments'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.adjustments')}
        </Typography>
      ),
    },
  },
  {
    name: 'deductible',
    label: t('invoiceServiceTable.deductible'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.deductible')}
        </Typography>
      ),
    },
  },
  {
    name: 'co_insurance',
    label: t('invoiceServiceTable.coInsurance'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.coInsurance')}
        </Typography>
      ),
    },
  },
  {
    name: 'co_pay',
    label: t('invoiceServiceTable.coPay'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.coPay')}
        </Typography>
      ),
    },
  },
  {
    name: 'patient_responsibility',
    label: t('invoiceServiceTable.balanceDue'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoiceServiceTable.balanceDue')}
        </Typography>
      ),
    },
  },
  {
    name: 'is_amount_paid_to_subscriber',
    label: '',
    options: {
      filter: false,
      sort: false,
      display: false,
    },
  },
];

export const getInvoiceReadOnlyPaymentTableColumns = (t: TFunction<'invoice'>) => [
  {
    name: 'mode',
    label: t('invoicePaymentTable.paymentType'),
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'date_received',
    label: t('invoicePaymentTable.dateReceived'),
    options: {
      filter: false,
      sort: true,
      customBodyRender: (val: Moment | string) =>
        val ? dateFormatConverter(val, API_DATE_FORMAT, DATE_DISPLAY_FORMAT) : '',
    },
  },
  {
    name: 'transaction_id',
    label: t('invoicePaymentTable.transactionId'),
    options: {
      filter: false,
      sort: true,
    },
  },
  {
    name: 'amount',
    label: t('invoicePaymentTable.amount'),
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
        },
      }),
      setCellProps: () => ({ style: { textAlign: 'right', width: '300px' } }),
      customBodyRender: (val: number) => (val || val === 0 ? usDollarFormatter.format(val) : ''),
      customHeadLabelRender: () => (
        <Typography color={FORTA_BLUE.primary} fontWeight={700}>
          {t('invoicePaymentTable.amount')}
        </Typography>
      ),
    },
  },
];

export const getInvoiceTableTheme = (theme: Themes) =>
  createTheme({
    ...primaryTheme,
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            cursor: 'default',
            textAlign: 'left',
            border: `1px solid ${FORTA_GRAY2}`,
            borderRadius: '10px',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          root: {
            display: 'none',
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            fontWeight: '700',
            color: getThemeColor(theme).primary,
            fontSize: '14px',
          },
          sortAction: {
            '& path': {
              color: FORTA_BLUE.primary,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingBottom: '0.5rem',
            paddingTop: '0.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            textAlign: 'left',
            fontSize: '14px',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h6: {
            fontSize: '21px !important',
            fontWeight: '700 !important',
          },
        },
      },
    },
  });

export const invoicePaymentTableColDefs: FHExcelTableColumnDef[] = [
  {
    name: 'amount',
    label: 'Amount',
    options: {
      filter: false,
      sort: true,
      setCellHeaderProps: () => ({
        style: {
          textAlign: 'right',
          paddingRight: '50px',
          width: '240px',
        },
      }),
    },
    component: FHExcelCellInputComponentTypes.INPUT,
    isMandatory: true,
    isFloatValueColumn: true,
    valueFormatter: (val) => (val || val === 0 ? usDollarFormatter.format(val) : ''),
  },
  {
    name: 'mode',
    label: 'Payment type',
    isMandatory: true,
    options: {
      filter: false,
      sort: true,
    },
    component: FHExcelCellInputComponentTypes.SELECT,
    dropdownOptions: Object.values(InvoicePaymentTypeEnum)
      .filter((val) => val !== InvoicePaymentTypeEnum.ONLINE_PAYMENT)
      .map((value: string) => ({
        value,
        label: value,
      })),
  },
  {
    name: 'date_received',
    label: 'Date received',
    options: {
      filter: false,
      sort: true,
    },
    component: FHExcelCellInputComponentTypes.DATE_PICKER,
    valueFormatter: (val: Moment | string) =>
      val ? dateFormatConverter(val, API_DATE_FORMAT, DATE_DISPLAY_FORMAT) : '',
  },
  {
    name: 'date_cleared',
    label: 'Date cleared',
    options: {
      filter: false,
      sort: true,
    },
    component: FHExcelCellInputComponentTypes.DATE_PICKER,
    valueFormatter: (val: Moment | string) =>
      val ? dateFormatConverter(val, API_DATE_FORMAT, DATE_DISPLAY_FORMAT) : '',
  },
  {
    name: 'transaction_id',
    label: 'Ref. #',
    options: {
      filter: false,
      sort: true,
    },
    component: FHExcelCellInputComponentTypes.INPUT,
  },
  {
    name: 'id',
    label: 'id',
    options: {
      filter: false,
      sort: false,
      display: false,
    },
  },
];

export const invoiceListTableOptions: MUIDataTableOptions = {
  filter: false,
  print: false,
  search: false,
  viewColumns: false,
  pagination: true,
  fixedHeader: false,
  sort: false,
  download: false,
  responsive: 'standard' satisfies Responsive,
  selectableRows: 'none' satisfies SelectableRows,
  onRowClick: (args: any) => history.navigate(`/billings/invoice-payment/${args[0]}`),
  customFooter: (
    rowCount: number,
    page: number,
    rowsPerPage: number,
    changeRowsPerPage: (page: string | number) => void,
    changePage: (newPage: number) => void,
  ) => {
    return (
      <InvoiceListFooter
        count={rowCount}
        page={page}
        rowsPerPage={rowsPerPage}
        changeRowsPerPage={changeRowsPerPage}
        changePage={changePage}
      />
    );
  },
};

export const invoiceTableTheme = (theme: Themes) =>
  createTheme({
    ...primaryTheme,
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            border: `1px solid ${FORTA_GRAY2}`,
            borderRadius: '10px',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            cursor: 'default',
            textAlign: 'left',
            border: 'none',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            '&.MuiTableRow-hover': {
              '&:hover': {
                transition: 'background-color 0.3s',
                cursor: 'pointer',
              },
            },
          },
        },
      },
      MUIDataTableToolbar: {
        styleOverrides: {
          root: {
            display: 'none',
          },
        },
      },
      MUIDataTableHeadCell: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            fontWeight: '700',
            color: getThemeColor(theme).primary,
            borderTop: '1px solid #e0e0e0',
            borderBottom: 'none',
          },
          contentWrapper: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            '::after': {
              content: `""`,
              backgroundImage: `url(${SortImg})`,
              backgroundRepeat: 'no-repeat',
              width: '1.2em',
              height: '1.2em',
              display: 'block',
              marginTop: '0.5em',
              marginRight: '4px',
            },
          },
          sortAction: {
            '& path': {
              color: FORTA_BLUE.primary,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingBottom: '0.5rem',
            paddingTop: '0.5rem',
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            textAlign: 'left',
            fontSize: '16px',
            '&:first-child': {
              borderLeft: `1px solid ${FORTA_GRAY2}`,
            },
            '&:last-child': {
              borderRight: `1px solid ${FORTA_GRAY2}`,
            },
            borderTop: `1px solid ${FORTA_GRAY2}`,
            borderCollapse: 'collapse',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h6: {
            fontSize: '21px !important',
            fontWeight: '700 !important',
          },
        },
      },
    },
  });

export const getInvoiceListTableColumns = (t: TFunction<'invoice'>) => [
  {
    name: 'id',
    label: 'ID',
    options: {
      filter: false,
      sort: false,
      display: false,
    },
  },
  {
    name: 'status',
    label: t('status'),
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: string) => {
        return InvoiceStatusRenderer(value, t);
      },
    },
  },
  {
    name: 'invoice_id',
    label: t('invoiceId'),
  },
  {
    name: 'amount_billed',
    label: t('amountBilled'),
    options: {
      setCellProps: () => ({ style: { textAlign: 'right', paddingRight: '50px' } }),
    },
  },
  {
    name: 'amount_due',
    label: t('amountDue'),
    options: {
      setCellProps: () => ({ style: { textAlign: 'right', paddingRight: '50px' } }),
    },
  },
  {
    name: 'date_sent',
    label: t('dateSent'),
  },
  {
    name: 'date_due',
    label: t('dueDate'),
    options: {
      customBodyRender: (value: any, tableMeta: any) => {
        const isOverdueAndUnpaid =
          new Date(value) < new Date() &&
          (tableMeta.rowData[1] === InvoiceStatus.NOT_PAID ||
            tableMeta.rowData[1] === InvoiceStatus.PARTIALLY_PAID);

        return (
          <Typography
            sx={{
              color: isOverdueAndUnpaid ? FORTA_ERROR : FORTA_BLACK,
              fontWeight: isOverdueAndUnpaid ? 700 : 400,
            }}
          >
            {value}
          </Typography>
        );
      },
    },
  },
  {
    name: 'actions',
    label: ' ',
    options: {
      display: true,
      customBodyRender: (value: any, tableMeta: any) => {
        const status = tableMeta.rowData[1];
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '1.5rem',
            }}
          >
            {(status === InvoiceStatus.NOT_PAID || status === InvoiceStatus.PARTIALLY_PAID) && (
              <FHButton
                variant="outlined"
                textTransform="none"
                sx={{ height: 30, borderRadius: '4px' }}
                data-testid="payNowButton"
                onClick={() =>
                  history.navigate(`/billings/invoice-payment/${tableMeta.rowData[0]}`)
                }
              >
                {t('payNow')}
              </FHButton>
            )}
            <IconButton
              sx={{
                p: 1,
                backgroundColor: FORTA_WHITE,
                borderRadius: '5px',
                height: 35,
                width: 120,
                border: `1px solid ${FORTA_GRAY2}`,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  transition: '0.3s',
                },
              }}
              data-testid="viewInvoiceButton"
              onClick={() => history.navigate(`/billings/invoice-payment/${tableMeta.rowData[0]}`)}
            >
              <Typography sx={{ fontSize: '14px', mr: 1, color: FORTA_BLUE.primary }}>
                {t('viewInvoice')}
              </Typography>
              <ArrowForwardIos
                sx={{
                  color: FORTA_BLUE.primary,
                  fontSize: '12px',
                }}
              />
            </IconButton>
          </Box>
        );
      },
    },
  },
];

export const dueDaysOptions = [
  {
    value: 30,
    label: getDueInDaysLabel(30),
  },
];

export const paymentStatusConfig = (invoiceId: string | undefined) => ({
  [InvoicePaymentStatus.SUCCESS]: {
    title: 'Payment successful',
    color: FORTA_SUCCESS,
    message: `Payment for invoice ${formatEntityId(
      invoiceId ?? '',
      ENTITY_PREFIX.Invoice,
    )} was successfully processed. Thank you!`,
    submitText: 'Okay',
    hideCancelButton: true,
  },
  [InvoicePaymentStatus.PROCESSING]: {
    title: 'Payment processing',
    color: FORTA_YELLOW,
    message: `Payment for invoice ${formatEntityId(
      invoiceId ?? '',
      ENTITY_PREFIX.Invoice,
    )} is currently processing. Please check back later.`,
    submitText: 'Okay',
    hideCancelButton: true,
  },
  [InvoicePaymentStatus.PENDING]: {
    title: 'Payment pending',
    color: FORTA_YELLOW,
    message: `Payment for invoice ${formatEntityId(
      invoiceId ?? '',
      ENTITY_PREFIX.Invoice,
    )} is pending. Please complete the payment process.`,
    submitText: 'Okay',
    hideCancelButton: true,
  },
  [InvoicePaymentStatus.FAILED]: {
    title: 'Payment failed',
    color: FORTA_ERROR,
    message: `Payment for invoice ${formatEntityId(
      invoiceId ?? '',
      ENTITY_PREFIX.Invoice,
    )} has failed. Please try again or contact support at billingsupport@fortahealth.com`,
    submitText: 'Okay',
    hideCancelButton: true,
  },
  [InvoicePaymentStatus.CANCELLED]: {
    title: 'Payment cancelled',
    color: FORTA_ERROR,
    message: `Payment for invoice ${formatEntityId(
      invoiceId ?? '',
      ENTITY_PREFIX.Invoice,
    )} was cancelled. Please try again to complete the payment.`,
    submitText: 'Okay',
    hideCancelButton: true,
  },
});
